import { useMemo, useState } from "react";
import { CATEGORIES, MENU_ITEMS } from "../lib/gomill-data";
import FoodCard from "../components/FoodCard";
import { Overline, Section } from "../components/Bits";
import { ShieldCheck, Leaf, Sparkles, Truck, Search } from "lucide-react";

const ATTRS = [
  { icon: Leaf, t: "Millet-Led Goodness" },
  { icon: ShieldCheck, t: "Hygienic & Safe" },
  { icon: Truck, t: "Delivered Fresh" },
  { icon: Sparkles, t: "Guilt-Free Indulgence" },
];

export default function Menu() {
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    let items = MENU_ITEMS;
    if (cat !== "all") items = items.filter((i) => i.category === cat);
    if (q.trim()) {
      const k = q.toLowerCase();
      items = items.filter((i) => i.name.toLowerCase().includes(k) || i.desc.toLowerCase().includes(k));
    }
    return items;
  }, [cat, q]);

  return (
    <>
      {/* Hero */}
      <section className="px-6 md:px-12 pt-14 md:pt-20 pb-10" data-testid="menu-hero">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Overline className="mb-5">Our Full Menu</Overline>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-gomill-green leading-[1.02] tracking-tight">
              Goodness in <span className="italic">every</span> bite.
            </h1>
            <p className="mt-5 text-lg text-gomill-muted max-w-xl">Wholesome millet meals made fresh, just for you. Bold flavours rebuilt for the way India eats today.</p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-xl">
              {ATTRS.map((a) => (
                <div key={a.t} className="flex items-center gap-2 bg-white border border-gomill-border rounded-2xl px-3 py-2.5">
                  <a.icon className="w-4 h-4 text-gomill-leaf shrink-0" />
                  <span className="text-xs font-semibold text-gomill-green">{a.t}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-square rounded-[32px] overflow-hidden bg-gomill-cream">
              <img src="https://customer-assets.emergentagent.com/job_gomill-healthy-bites/artifacts/3nw02j0z_ChatGPT%20Image%20May%2026%2C%202026%2C%2006_15_35%20PM.png" alt="GoMill truck with millet meals" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Sticky filter bar */}
      <div className="sticky top-20 md:top-24 z-30 bg-gomill-bg/85 backdrop-blur border-y border-gomill-border" data-testid="menu-filter-bar">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-3 flex items-center gap-3 overflow-x-auto gm-no-scrollbar">
          <div className="relative shrink-0">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gomill-muted" />
            <input
              data-testid="menu-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search dishes…"
              className="pl-10 pr-4 py-2 rounded-full bg-white border border-gomill-border focus:ring-2 focus:ring-gomill-mustard focus:outline-none text-sm w-44 md:w-56"
            />
          </div>
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setCat(c.id)}
              data-testid={`menu-cat-${c.id}`}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                cat === c.id
                  ? "bg-gomill-green text-white border-gomill-green"
                  : "bg-white text-gomill-green border-gomill-border hover:bg-gomill-cream"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <Section>
        <div className="flex items-center justify-between mb-8">
          <p className="text-sm text-gomill-muted">Showing <strong className="text-gomill-green">{filtered.length}</strong> items</p>
        </div>
        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <p className="font-serif text-3xl text-gomill-green">Nothing here yet.</p>
            <p className="text-gomill-muted mt-2">Try clearing your filters.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="menu-grid">
            {filtered.map((m) => <FoodCard key={m.id} item={m} />)}
          </div>
        )}
      </Section>
    </>
  );
}
